// Tipo de local de la tienda online y pestaña `?seccion=` (Restaurante | Mercado).
// MÓDULO PURO, sin imports de servidor: lo usan la carga de datos (servidor) y la portada (cliente).
import { SECCION_MERCADO, SECCION_PARAM } from "@/lib/storefront/href"
import { dropEmptyCategories } from "@/lib/microsite/catalogGate"
import { isRetailWeightItem } from "@/lib/microsite/retailLine"

export type StorefrontKind = "restaurant" | "mixed" | "grocery" | "grocery_empty"
export type StorefrontSection = "restaurant" | "market"

/**
 * Tope fijo de la barra de categorías (y de todo lo que se pega) cuando hay pestañas
 * «Restaurante | Mercado»: debajo de la nav, la barra de entrega Y las pestañas. Única
 * expresión; sin pestañas, `MenuSection` usa la de siempre (sin `--ms-tabs-h`).
 */
export const STOREFRONT_TABS_STICKY_TOP =
  "calc(var(--ms-nav-h, 56px) + var(--ms-fulfill-h, 0px) + var(--ms-tabs-h, 0px))"

/**
 * Único predicado de «sin platos»: solo cuenta si la consulta de platos SÍ cargó. Si falló,
 * nunca se concluye que el local es un súper. (`loadMicrositeData` lo usa para `groceryStore`.)
 */
export function hasNoDishes(args: { dishesLoaded: boolean; dishCount: number }): boolean {
  return args.dishesLoaded && args.dishCount === 0
}

/** Única regla de «producto de mercado»: `is_retail === true`. Cualquier otro valor es un plato. */
export function isRetailItem(item: { is_retail?: boolean | null }): boolean {
  return item.is_retail === true
}

export function storefrontKindOf(args: { dishesLoaded: boolean; dishCount: number; marketCount: number }): StorefrontKind {
  if (!args.dishesLoaded) return "restaurant"
  if (hasNoDishes(args)) return args.marketCount > 0 ? "grocery" : "grocery_empty"
  return args.marketCount > 0 ? "mixed" : "restaurant"
}

export function splitStorefrontCatalog<
  C extends { id: string },
  I extends { category_id: string | null; is_retail?: boolean | null; pricing_mode?: string | null; weight_unit?: string | null },
>(
  categories: C[],
  items: I[],
): {
  dishes: { categories: C[]; items: I[] }
  market: { categories: C[]; items: I[]; hasWeighted: boolean }
} {
  const marketItems = items.filter(isRetailItem)
  const dishItems = items.filter((i) => !isRetailItem(i))
  return {
    dishes: { categories: dropEmptyCategories(categories, dishItems), items: dishItems },
    market: {
      categories: dropEmptyCategories(categories, marketItems),
      items: marketItems,
      hasWeighted: marketItems.some(isRetailWeightItem),
    },
  }
}

/** Solo el valor exacto `mercado` (string) abre Mercado; todo lo demás es Restaurante. */
export function sectionFromParam(value: unknown): StorefrontSection {
  return value === SECCION_MERCADO ? "market" : "restaurant"
}

function applySection(params: URLSearchParams, section: StorefrontSection): void {
  if (section === "market") params.set(SECCION_PARAM, SECCION_MERCADO)
  else params.delete(SECCION_PARAM)
}

/** Aplica la sección a una búsqueda (`?a=b` o `a=b`), conservando el resto y su orden. */
export function searchWithSection(search: string, section: StorefrontSection): string {
  const params = new URLSearchParams(search)
  applySection(params, section)
  const qs = params.toString()
  return qs ? `?${qs}` : ""
}

/** Igual sobre un href completo (absoluto o relativo): conserva ruta, resto de la búsqueda y `#hash`. */
export function urlWithSection(href: string, section: StorefrontSection): string {
  const hashAt = href.indexOf("#")
  const hash = hashAt >= 0 ? href.slice(hashAt) : ""
  const noHash = hashAt >= 0 ? href.slice(0, hashAt) : href
  const qAt = noHash.indexOf("?")
  const path = qAt >= 0 ? noHash.slice(0, qAt) : noHash
  const search = qAt >= 0 ? noHash.slice(qAt) : ""
  return `${path}${searchWithSection(search, section)}${hash}`
}

/**
 * URL tras canjear un `?prefill=` en la portada (con la bandera): solo el pathname, como main,
 * pero conservando `?seccion=mercado` para que recargar no devuelva a Restaurante.
 */
export function pathnameKeepingSection(pathname: string, search: string): string {
  return urlWithSection(pathname, sectionFromParam(new URLSearchParams(search).get(SECCION_PARAM)))
}

/**
 * Qué props recibe la portada. Sin la bandera, nada (el restaurante no recibe props nuevas).
 * Con bandera pero payload viejo (caché sin `storefrontKind`) → restaurante sin pestañas.
 * Solo el mixto toma la sección del parámetro; en súper se ignora.
 */
export function resolveStorefrontView(
  data: { retailV2?: boolean; storefrontKind?: StorefrontKind },
  seccion: unknown,
): { storefrontKind?: StorefrontKind; initialSection?: StorefrontSection } {
  if (!data.retailV2) return {}
  const storefrontKind = data.storefrontKind ?? "restaurant"
  if (storefrontKind === "mixed") return { storefrontKind, initialSection: sectionFromParam(seccion) }
  return { storefrontKind }
}

export function menuAnchorId(kind: StorefrontKind | undefined): "storefront-tabs" | "menu" {
  return kind === "mixed" ? "storefront-tabs" : "menu"
}

/** Solo el mixto y el súper llevan política de entrega propia en el pie; el restaurante, la de main. */
export function usesLocalFulfillmentPolicy(kind: StorefrontKind | undefined): boolean {
  return kind === "mixed" || kind === "grocery" || kind === "grocery_empty"
}
